import { CalendarIcon, Link2Icon, PlusIcon, XIcon } from "lucide-react";
import { useState } from "react";
import type {
  CmmdBoardTask,
  CmmdTaskWorkflow,
  CreateCmmdBoardTaskParams,
  UpdateCmmdBoardTaskParams,
} from "@cmmd-forge/contracts";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { getStatusConfig } from "~/components/command-center/statusConfig";
import { ProjectTaskDetailSection as Section } from "./ProjectTaskDetailSection";
import { ProjectTaskPicker } from "./ProjectTaskPicker";
import { TaskAssigneeIdentity } from "./atoms/TaskAssigneeIdentity";
import { TaskStatusGlyph } from "./atoms/TaskStatusGlyph";
import { TaskWorkItemTypeIcon } from "./atoms/TaskWorkItemTypeIcon";
import { initialCreateStatusSelection } from "./projectTaskCreateOptions";
import { formatTaskDueLabel } from "./taskPresentation";
import { RESPONSIVE_ICON_BUTTON_CLASS } from "~/lib/touchTarget";

interface RelationshipMetadataProps {
  readonly task: CmmdBoardTask;
}

function relationshipAssignee(task: CmmdBoardTask): {
  readonly agentId?: string;
  readonly avatarUrl: string | null;
  readonly kind: "agent" | "human" | null;
  readonly name: string | null;
} {
  if (task.assignment?.kind === "agent_member") {
    return {
      agentId: task.assignment.agentId,
      avatarUrl: null,
      kind: "agent",
      name: task.assignment.displayName,
    };
  }
  return {
    avatarUrl: task.assigneeAvatar,
    kind: task.assigneeName ? "human" : null,
    name: task.assigneeName,
  };
}

function RelationshipDueDate({ dueLabel }: { readonly dueLabel: string | null }) {
  return dueLabel ? (
    <span
      className="hidden items-center gap-1 font-mono tabular-nums md:inline-flex"
      aria-label={`Due ${dueLabel}`}
    >
      <CalendarIcon className="size-3" aria-hidden />
      {dueLabel}
    </span>
  ) : null;
}

function RelationshipMetadata({ task }: RelationshipMetadataProps) {
  const dueLabel = formatTaskDueLabel(task.dueDate);
  const status = getStatusConfig(task.status);
  const assignee = relationshipAssignee(task);
  return (
    <span className="col-span-2 col-start-2 flex min-w-0 items-center gap-2 text-sm text-muted-foreground md:col-auto md:shrink-0 md:text-[11px]">
      <span className="inline-flex items-center gap-1">
        <TaskStatusGlyph status={task.status} />
        <span aria-hidden>{status.label}</span>
      </span>
      <RelationshipDueDate dueLabel={dueLabel} />
      <TaskAssigneeIdentity
        name={assignee.name}
        avatarUrl={assignee.avatarUrl}
        kind={assignee.kind}
        agentId={assignee.agentId}
        className="size-5 text-[9px]"
      />
    </span>
  );
}

function RelationshipTaskContent({ task }: RelationshipMetadataProps) {
  return (
    <>
      <TaskWorkItemTypeIcon workItemType={task.workItemType} />
      <span className="shrink-0 font-mono text-meta tabular-nums text-muted-foreground">
        #{task.id}
      </span>
      <span className="min-w-0 flex-1 truncate">{task.title}</span>
      <RelationshipMetadata task={task} />
    </>
  );
}

export interface ProjectTaskRelationshipsProps {
  readonly task: CmmdBoardTask;
  readonly allTasks: readonly CmmdBoardTask[];
  readonly pending: boolean;
  readonly creating: boolean;
  readonly mutationDisabled: boolean;
  readonly workflow: CmmdTaskWorkflow | null;
  readonly onOpenTask: (id: number) => void;
  readonly onUpdate: (id: number, patch: UpdateCmmdBoardTaskParams) => Promise<boolean>;
  readonly onCreateTask: (params: CreateCmmdBoardTaskParams) => Promise<boolean>;
}

type ProjectTaskRelationshipSectionProps = ProjectTaskRelationshipsProps;

export function subtaskCreateParams(
  title: string,
  parentTaskId: number,
  workflow: CmmdTaskWorkflow | null,
): CreateCmmdBoardTaskParams {
  const statusSelection = initialCreateStatusSelection(workflow);
  return {
    title,
    parentTaskId,
    status: statusSelection.status,
    ...(statusSelection.workflowStageId != null
      ? { workflowStageId: statusSelection.workflowStageId }
      : {}),
  };
}

export function ProjectTaskSubtasks(props: ProjectTaskRelationshipSectionProps) {
  const [subtaskTitle, setSubtaskTitle] = useState("");
  const createSubtask = async () => {
    const title = subtaskTitle.trim();
    if (!title || props.mutationDisabled) return;
    const created = await props.onCreateTask(
      subtaskCreateParams(title, props.task.id, props.workflow),
    );
    if (created) setSubtaskTitle("");
  };

  return (
    <Section
      icon={<Link2Icon className="size-3.5" aria-hidden />}
      label={`Subtasks (${props.task.childCount})`}
    >
      <div className="rounded-lg border border-border">
        <form
          className="flex items-center gap-[8px] border-b border-border p-2 md:gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            void createSubtask();
          }}
        >
          <Input
            aria-label="New subtask title"
            value={subtaskTitle}
            disabled={props.pending || props.creating || props.mutationDisabled}
            onChange={(event) => setSubtaskTitle(event.currentTarget.value)}
            placeholder="Add a subtask"
            size="sm"
          />
          <Button
            type="submit"
            size="sm"
            variant="ghost"
            className="min-h-touch md:min-h-7"
            disabled={
              !subtaskTitle.trim() || props.pending || props.creating || props.mutationDisabled
            }
          >
            <PlusIcon aria-hidden />
            Add
          </Button>
        </form>
        {props.task.children.length > 0 ? (
          <ul className="divide-y divide-border">
            {props.task.children.map((child) => (
              <li key={child.id}>
                <button
                  type="button"
                  onClick={() => props.onOpenTask(child.id)}
                  className="grid min-h-touch w-full grid-cols-[auto_auto_minmax(0,1fr)] items-center gap-x-2 gap-y-1 px-3 py-2 text-left text-sm transition-colors hover:bg-muted md:flex md:min-h-10"
                >
                  <RelationshipTaskContent task={child} />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="px-3 py-3 text-sm text-muted-foreground">No subtasks yet.</p>
        )}
      </div>
    </Section>
  );
}

export function ProjectTaskDependencies(props: ProjectTaskRelationshipSectionProps) {
  const taskById = new Map(props.allTasks.map((candidate) => [candidate.id, candidate]));
  const dependencyOptions = props.allTasks.filter(
    (candidate) => candidate.id !== props.task.id && !props.task.dependsOn.includes(candidate.id),
  );
  const dependencyPickerOptions = dependencyOptions.map((candidate) => ({
    value: String(candidate.id),
    label: `#${candidate.id} ${candidate.title}`,
  }));

  return (
    <Section icon={<Link2Icon className="size-3.5" aria-hidden />} label="Dependencies">
      <div className="rounded-lg border border-border">
        <div className="border-b border-border p-2">
          <ProjectTaskPicker
            ariaLabel="Add dependency"
            value={null}
            emptyLabel="Add dependency…"
            options={dependencyPickerOptions}
            disabled={props.pending || dependencyOptions.length === 0}
            onValueChange={(value) => {
              const dependencyId = value === null ? null : Number(value);
              if (!dependencyId) return;
              void props.onUpdate(props.task.id, {
                dependsOn: [...props.task.dependsOn, dependencyId],
              });
            }}
          />
        </div>
        {props.task.dependsOn.length > 0 ? (
          <ul className="divide-y divide-border">
            {props.task.dependsOn.map((dependencyId) => {
              const dependency = taskById.get(dependencyId);
              return (
                <li key={dependencyId} className="flex min-h-touch items-center">
                  <button
                    type="button"
                    onClick={() => (dependency ? props.onOpenTask(dependency.id) : undefined)}
                    disabled={!dependency}
                    className="grid min-h-touch min-w-0 flex-1 grid-cols-[auto_auto_minmax(0,1fr)] items-center gap-x-2 gap-y-1 px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted disabled:text-muted-foreground md:flex md:min-h-10"
                  >
                    {dependency ? (
                      <RelationshipTaskContent task={dependency} />
                    ) : (
                      <>
                        <Link2Icon className="size-3.5 text-muted-foreground" aria-hidden />
                        <span className="font-mono text-meta tabular-nums">#{dependencyId}</span>
                        <span className="truncate">Unavailable task</span>
                      </>
                    )}
                  </button>
                  <Button
                    type="button"
                    size="icon-xs"
                    variant="ghost"
                    className={`mr-1 ${RESPONSIVE_ICON_BUTTON_CLASS}`}
                    aria-label={`Remove dependency ${dependencyId}`}
                    disabled={props.pending}
                    onClick={() =>
                      void props.onUpdate(props.task.id, {
                        dependsOn: props.task.dependsOn.filter((id) => id !== dependencyId),
                      })
                    }
                  >
                    <XIcon aria-hidden />
                  </Button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="px-3 py-3 text-sm text-muted-foreground">No blocking dependencies.</p>
        )}
        {props.task.blockedByCount > 0 ? (
          <p className="border-t border-border px-3 py-2 text-xs text-warning-foreground">
            This task blocks {props.task.blockedByCount} other work item
            {props.task.blockedByCount === 1 ? "" : "s"}.
          </p>
        ) : null}
      </div>
    </Section>
  );
}

export function ProjectTaskRelationships(props: ProjectTaskRelationshipsProps) {
  return (
    <div className="space-y-5">
      <ProjectTaskSubtasks {...props} />
      <ProjectTaskDependencies {...props} />
    </div>
  );
}
